import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar as CalIcon, Video as VideoIcon, FileText, Upload, ArrowLeft, ArrowRight,
  Check, ShieldCheck, Phone, Download, Eye, MessageSquare, Stethoscope, Clock,
  Copy, Users as UsersIcon, Plus, Trash2, UserRound, IdCard,
  LayoutDashboard, Menu as MenuIcon, Home, LogOut, Pencil, Mail, Lock,
  ListChecks, ChevronDown, Megaphone, Building2, Sparkles, MapPin, X, Star, BellRing, AlertTriangle,
} from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { COUNTRIES } from "@/data/countries";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/lib/toast";
import PortalShell from "@/components/portal/PortalShell";
import Calendar from "@/components/portal/Calendar";
import Video from "@/components/portal/Video";
import Chat from "@/components/portal/Chat";
import Inbox from "@/components/portal/Inbox";
import MailView from "@/components/portal/PortalMail";
import NotificationsBell from "@/components/portal/NotificationsBell";
import { useMessaging } from "@/context/MessagingContext";
import { COMPLAINTS, NEEDS, INSTRUCTIONS } from "@/data/content";
import { pay } from "@/lib/payment";
import { AddToCalendar, CalendarSubscribe } from "@/components/portal/CalendarSync";
import PatientMyAppointments from "./MyAppointments";
import CallbackRequestModal from "@/components/site/CallbackRequestModal";

/* --------------------------------- Auth --------------------------------- */
function PatientAuth({ onDone }) {
  const { refresh } = useAuth();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [stage, setStage] = useState("form"); // form | otp
  const [form, setForm] = useState({ name: "", mobile: "", email: "", password: "", age: "", sex: "", country: "", dial_code: "", consent: false });
  const [otp, setOtp] = useState("");
  const [newPass, setNewPass] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [err, setErr] = useState("");
  const [cbOpen, setCbOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const login = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/login", { email: form.email, password: form.password });
      const u = await refresh();
      if (u?.role !== "patient") { setErr("This is the patient portal. Please use the correct login."); return; }
      onDone();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendOtp = async (purpose) => {
    setErr(""); 
    if (purpose === "register" && !form.consent) { setErr("Please accept the privacy notice (DPDP Act, 2023) to continue."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/send-otp", { purpose, role: "patient", ...form });
      if (data.dev_otp) setDevOtp(data.dev_otp);
      setStage("otp");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setErr(""); setBusy(true);
    try {
      const body = { email: form.email, code: otp };
      if (mode === "forgot") body.new_password = newPass;
      const { data } = await api.post("/auth/verify-otp", body);
      if (mode === "forgot") { toast("Password updated — please sign in."); setMode("login"); setStage("form"); }
      else { await refresh(); onDone(); }
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="auth">
      <div className="auth-art">
        <div className="logo" style={{ marginBottom: 22 }}>2</div>
        <h2>Your health records, your second opinion — in one secure place.</h2>
        <p>Book consultations, share reports privately, and keep every specialist opinion together.</p>
        {["NMC-registered specialists", "DPDP Act 2023 — data protected", "Telemedicine Guidelines 2020"].map(t => (
          <div className="pt" key={t}><Check size={16} /> {t}</div>
        ))}
      </div>

      <div className="auth-form">
        <div className="tabs">
          <button className={`tab ${mode === "login" ? "on" : ""}`} onClick={() => { setMode("login"); setStage("form"); setErr(""); }} data-testid="tab-login">Sign in</button>
          <button className={`tab ${mode === "register" ? "on" : ""}`} onClick={() => { setMode("register"); setStage("form"); setErr(""); }} data-testid="tab-register">Create account</button>
        </div>

        {err && <p className="err" data-testid="auth-error">{err}</p>}
        {devOtp && stage === "otp" && <div className="demohint" data-testid="dev-otp">Demo mode — your code is <b>{devOtp}</b></div>}

        {stage === "form" && mode === "login" && (
          <>
            <div className="field"><label>Email</label><input className="in" value={form.email} onChange={set("email")} data-testid="login-email" /></div>
            <div className="field"><label>Password</label><input className="in" type="password" value={form.password} onChange={set("password")} data-testid="login-password" /></div>
            <button className="btn btn-ink full" onClick={login} disabled={busy} data-testid="login-submit">{busy ? "Signing in…" : "Sign in"}</button>
            <p className="note" style={{ cursor: "pointer" }} onClick={() => { setMode("forgot"); setStage("form"); }}>Forgot your password?</p>
          </>
        )}

        {stage === "form" && mode === "register" && (
          <>
            <div className="field"><label>Full name</label><input className="in" value={form.name} onChange={set("name")} data-testid="reg-name" /></div>
            <div className="field two"><div><label>Age <span style={{ color: "var(--slate)", fontWeight: 400 }}>(optional)</span></label><input className="in" value={form.age} onChange={set("age")} data-testid="reg-age" placeholder="e.g. 34" /></div>
              <div><label>Sex <span style={{ color: "var(--slate)", fontWeight: 400 }}>(optional)</span></label>
                <select className="in" value={form.sex} onChange={set("sex")} data-testid="reg-sex">
                  <option value="">Select</option><option>Male</option><option>Female</option><option>Other</option>
                </select></div>
            </div>
            <div className="field"><label>Country</label>
              <select className="in" value={form.country} onChange={(e) => { const c = COUNTRIES.find(x => x.name === e.target.value); setForm({ ...form, country: e.target.value, dial_code: c ? c.dial : form.dial_code }); }} data-testid="reg-country">
                <option value="">Select your country</option>
                {COUNTRIES.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div className="field"><label>Mobile number</label>
              <div className="phone-row">
                <select className="in phone-code" value={form.dial_code} onChange={set("dial_code")} data-testid="reg-dial-code">
                  <option value="">Code</option>
                  {COUNTRIES.filter(c => c.dial !== "+").map(c => <option key={c.code} value={c.dial}>{c.dial} {c.code}</option>)}
                </select>
                <input className="in" value={form.mobile} onChange={set("mobile")} data-testid="reg-mobile" placeholder="Phone number" />
              </div>
            </div>
            <div className="field"><label>Email</label><input className="in" value={form.email} onChange={set("email")} data-testid="reg-email" /></div>
            <div className="field"><label>Password (min 8 chars)</label><input className="in" type="password" value={form.password} onChange={set("password")} data-testid="reg-password" /></div>
            <label className="consent-row" data-testid="reg-consent-row">
              <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="reg-consent" />
              <span>I consent to 2nd Opinion processing my personal &amp; health data to provide consultations, in accordance with the <b>Privacy Policy</b> and the <b>DPDP Act, 2023</b>. I can request correction or erasure anytime.</span>
            </label>
            <button className="btn btn-green full" onClick={() => sendOtp("register")} disabled={busy} data-testid="reg-submit">{busy ? "Sending code…" : "Verify email"}</button>
          </>
        )}

        {stage === "form" && mode === "forgot" && (
          <>
            <p className="lead" style={{ marginBottom: 14 }}>Enter your email and we’ll send a verification code.</p>
            <div className="field"><label>Email</label><input className="in" value={form.email} onChange={set("email")} data-testid="forgot-email" /></div>
            <button className="btn btn-ink full" onClick={() => sendOtp("reset")} disabled={busy}>{busy ? "Sending…" : "Send code"}</button>
            <p className="note" style={{ cursor: "pointer" }} onClick={() => setMode("login")}>Back to sign in</p>
          </>
        )}

        {stage === "otp" && (
          <>
            <p className="lead" style={{ marginBottom: 14 }}>Enter the 6-digit code sent to <b>{form.email}</b>.</p>
            <div className="field"><label>Verification code</label><input className="in" value={otp} onChange={e => setOtp(e.target.value)} maxLength={6} data-testid="otp-input" /></div>
            {mode === "forgot" && <div className="field"><label>New password</label><input className="in" type="password" value={newPass} onChange={e => setNewPass(e.target.value)} data-testid="otp-newpass" /></div>}
            <button className="btn btn-green full" onClick={verify} disabled={busy} data-testid="otp-verify">{busy ? "Verifying…" : "Verify & continue"}</button>
            <p className="note" style={{ cursor: "pointer" }} onClick={() => setStage("form")}>Back</p>
          </>
        )}

        <div className="auth-callback-cta" data-testid="auth-callback-cta">
          <span>Need help signing in or registering?</span>
          <button type="button" className="auth-callback-btn" onClick={() => setCbOpen(true)} data-testid="auth-callback-trigger">
            Request a callback from customer support
          </button>
        </div>
      </div>
      {cbOpen && <CallbackRequestModal role="patient" onClose={() => setCbOpen(false)} />}
    </div>
  );
}

/* ------------------------------ Booking flow ----------------------------- */
function BookFlow({ user, onBooked, onCancel }) {
  const prefill = (() => { try { return JSON.parse(sessionStorage.getItem("booking") || "{}"); } catch { return {}; } })();
  const [step, setStep] = useState(1);
  const [complaint, setComplaint] = useState(prefill.complaint || "");
  const [need, setNeed] = useState(prefill.need || "Video consultation");
  const [specialty, setSpecialty] = useState(prefill.specialty || "");
  const [specialties, setSpecialties] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [doctor, setDoctor] = useState(prefill.doctor || null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  // Who is this consultation FOR: "self" or a family-member id
  const [forWho, setForWho] = useState(prefill.for_member_id || "self");
  const [family, setFamily] = useState([]);
  // post-booking add-info step
  const [bookedApptId, setBookedApptId] = useState(null);
  const [bookedFiles, setBookedFiles] = useState([]);
  const [postNotes, setPostNotes] = useState("");
  // waitlist (notify me when a slot opens)
  const [wlJoined, setWlJoined] = useState(false);
  const [wlBusy, setWlBusy] = useState(false);
  // AI symptom checker / triage (same engine as the public Book page)
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  // Fee breakdown — pulled from /patient/me/discount; updates when consult type
  // changes or when the patient opens the review screen.
  const BASE_FEE = (() => {
    const n = parseInt(String(doctor?.consult_fee || "").replace(/\D/g, ""), 10);
    return Number.isNaN(n) || n <= 0 ? 999 : n;
  })();
  const [discountPct, setDiscountPct] = useState(0);
  useEffect(() => {
    if (!user?.subscribed) { setDiscountPct(0); return; }
    api.get("/patient/me/discount").then(r => {
      const product = (need || "").toLowerCase().includes("clinic") ? "clinic_consult" : "online_consult";
      setDiscountPct(r.data?.[product] || 0);
    }).catch(() => setDiscountPct(0));
  }, [user?.subscribed, need]);
  const discountAmount = Math.round((BASE_FEE * discountPct) / 100);
  const finalFee = BASE_FEE - discountAmount;

  useEffect(() => { api.get("/public/specialties").then(r => setSpecialties(r.data.specialties || [])); }, []);
  useEffect(() => { api.get("/patient/family").then(r => setFamily(r.data.members || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (specialty) api.get(`/public/doctors?specialty=${encodeURIComponent(specialty)}`).then(r => setDoctors(r.data.doctors || []));
  }, [specialty]);
  useEffect(() => {
    if (doctor && date) api.get(`/availability?doctor_id=${doctor.id}&date=${date}`).then(r => setSlots(r.data.slots || []));
  }, [doctor, date]);
  useEffect(() => { setWlJoined(false); }, [doctor, date]);
  useEffect(() => { sessionStorage.removeItem("booking"); }, []);

  const runTriage = async () => {
    if (aiText.trim().length < 4) { toast("Please describe your symptoms first"); return; }
    setAiBusy(true); setAiResult(null);
    try {
      const { data } = await api.post("/triage", { symptom: aiText });
      setAiResult(data);
    } catch (e) {
      toast(formatApiError(e.response?.data?.detail) || "AI triage is unavailable right now.");
    } finally { setAiBusy(false); }
  };
  const applySuggestion = (sp) => {
    setSpecialty(sp); setDoctor(null);
    if (!complaint) setComplaint(aiText.slice(0, 80));
    toast(`Specialty set to ${sp}`);
  };

  const joinWaitlist = async () => {
    setWlBusy(true);
    try {
      await api.post("/waitlist", { doctor_id: doctor.id, date });
      setWlJoined(true);
      toast("You're on the waitlist — we'll notify you if a slot opens ✓");
    } catch (e) {
      toast(formatApiError(e.response?.data?.detail) || "Couldn't join the waitlist.");
    } finally { setWlBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const proof = await pay(api, { description: `${need} · ${doctor.name}`, name: user.name, email: user.email, consult_type: need, doctor_id: doctor.id });
      const { data } = await api.post("/appointments", {
        symptom: complaint, specialty, doctor_id: doctor.id, date, time,
        consult_type: need, order_id: proof.order_id, payment_id: proof.payment_id, signature: proof.signature,
        for_member_id: forWho === "self" ? "" : forWho,
      });
      toast("Appointment confirmed ✓");
      setBookedApptId(data.appointment.id);
      setStep(4);
    } catch (e) {
      toast(e.response ? formatApiError(e.response.data?.detail) : e.message);
    } finally { setBusy(false); }
  };

  const uploadPostBooking = async (e) => {
    const fs = Array.from(e.target.files || []);
    e.target.value = "";
    if (!fs.length) return;
    const remaining = 10 - bookedFiles.length;
    if (fs.length > remaining) { toast(`You can upload up to 10 documents (${remaining} more).`); return; }
    const fd = new FormData(); fs.forEach(f => fd.append("files", f));
    try {
      const { data } = await api.post(`/appointments/${bookedApptId}/files`, fd);
      setBookedFiles(data.files || []);
      toast("Documents uploaded ✓");
    } catch (err) { toast(formatApiError(err.response?.data?.detail)); }
  };

  const removePostBooking = async (fid, fname) => {
    if (!window.confirm(`Remove "${fname}"?`)) return;
    try {
      const { data } = await api.delete(`/appointments/${bookedApptId}/files/${fid}`);
      setBookedFiles(data.files || []);
      toast("Removed ✓");
    } catch (err) { toast(formatApiError(err.response?.data?.detail)); }
  };

  const saveNotesAndFinish = async () => {
    setBusy(true);
    try {
      if (postNotes.trim()) {
        await api.put(`/appointments/${bookedApptId}/patient-notes`, { patient_notes: postNotes });
      }
      onBooked(bookedApptId);
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button className="back" onClick={onCancel} data-testid="book-back"><ArrowLeft size={15} /> Back to dashboard</button>
      <h2 className="h" style={{ fontSize: 26, margin: "0 0 4px" }}>Book a consultation</h2>
      <p className="lead" style={{ marginBottom: 18 }}>
        Fee: {discountAmount > 0
          ? <><s style={{ color: "var(--slate)" }}>₹{BASE_FEE}</s> <b style={{ color: "var(--green-deep)" }}>₹{finalFee}</b> · {discountPct}% Premium subscriber discount</>
          : <>₹{BASE_FEE}</>} · Secure payment
      </p>
      <div className="psteps">
        {[1, 2, 3, 4].map(s => <div key={s} className={`pstep ${step > s ? "done" : step === s ? "now" : ""}`} />)}
      </div>

      {step === 1 && (
        <div className="card">
          <div className="ai-triage" data-testid="pp-ai-triage">
            <div className="ai-triage-head">
              <span className="ai-triage-icon"><Sparkles size={16} /></span>
              <div>
                <h4>Not sure which specialist you need?</h4>
                <p>Describe your symptoms and our AI will suggest the right specialty.</p>
              </div>
            </div>
            <textarea className="in ai-triage-input" rows={2} value={aiText}
              onChange={e => setAiText(e.target.value)}
              placeholder="e.g. persistent headache with blurred vision for two weeks…"
              data-testid="pp-ai-triage-input" />
            <button className="btn btn-ink" onClick={runTriage} disabled={aiBusy} data-testid="pp-ai-triage-btn">
              <Sparkles size={15} /> {aiBusy ? "Analysing…" : "Suggest a specialist"}
            </button>
            {aiResult && (
              <div className="ai-triage-result" data-testid="pp-ai-triage-result">
                {aiResult.urgent && (
                  <div className="ai-urgent" data-testid="pp-ai-triage-urgent">
                    <AlertTriangle size={15} /> Your symptoms may need urgent care. If this is an emergency, please call your local emergency number now.
                  </div>
                )}
                <p className="ai-rec">Recommended: <b>{aiResult.specialty}</b></p>
                <p className="ai-reason">{aiResult.reason}</p>
                <div className="ai-chips">
                  <button className="ai-chip primary" onClick={() => applySuggestion(aiResult.specialty)} data-testid="pp-ai-triage-use">
                    Use {aiResult.specialty}
                  </button>
                  {(aiResult.alternatives || []).map(a => (
                    <button className="ai-chip" key={a} onClick={() => applySuggestion(a)} data-testid={`pp-ai-triage-alt-${a}`}>{a}</button>
                  ))}
                </div>
                <p className="ai-disclaimer">This is an AI suggestion, not a medical diagnosis.</p>
              </div>
            )}
          </div>
          <div className="field">
            <label>Who is this consultation for?</label>
            <div className="for-who" data-testid="flow-forwho">
              <button type="button"
                      className={`for-who-pill ${forWho === "self" ? "active" : ""}`}
                      onClick={() => setForWho("self")}
                      data-testid="flow-forwho-self">
                <UserRound size={14} /> Myself
                <small>{user.name}</small>
              </button>
              {family.map(m => (
                <button type="button" key={m.id}
                        className={`for-who-pill ${forWho === m.id ? "active" : ""}`}
                        onClick={() => setForWho(m.id)}
                        data-testid={`flow-forwho-${m.id}`}>
                  <UsersIcon size={14} /> {m.name}
                  <small>{m.relationship}{m.age ? ` · ${m.age}y` : ""}{m.sex ? ` · ${m.sex}` : ""}</small>
                </button>
              ))}
            </div>
            {family.length === 0 && (
              <small style={{ color: "var(--slate)", fontSize: 12 }}>
                You can also book for a registered family member — add them under <b>My family</b>.
              </small>
            )}
          </div>
          <div className="field"><label>What brings you here?</label>
            <select className="in" value={complaint} onChange={e => setComplaint(e.target.value)} data-testid="flow-complaint">
              <option value="">Select your main concern</option>{COMPLAINTS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="field"><label>What do you need?</label>
            <select className="in" value={need} onChange={e => setNeed(e.target.value)} data-testid="flow-need">
              {NEEDS.map(n => <option key={n}>{n}</option>)}
            </select>
          </div>
          <div className="field"><label>Specialty</label>
            <select className="in" value={specialty} onChange={e => { setSpecialty(e.target.value); setDoctor(null); }} data-testid="flow-specialty">
              <option value="">Select a specialty</option>{specialties.map(s => <option key={s.id}>{s.name}</option>)}
            </select>
          </div>
          {specialty && (
            <div style={{ marginTop: 8 }}>
              <label style={{ fontSize: 12.5, color: "var(--slate)", display: "block", marginBottom: 8 }}>Choose your specialist</label>
              {doctors.map(d => (
                <div className={`docrow ${doctor?.id === d.id ? "" : ""}`} key={d.id}
                  onClick={() => setDoctor(d)}
                  style={{ cursor: "pointer", borderColor: doctor?.id === d.id ? "var(--pine)" : "var(--line)" }}
                  data-testid={`flow-doctor-${d.id}`}>
                  <div className="ic" style={{ width: 38, height: 38, borderRadius: 11 }}><Stethoscope size={18} /></div>
                  <div className="m"><h4>{d.name}</h4><p>{d.specialty} · {d.reg_no}</p></div>
                  {doctor?.id === d.id && <Check size={18} color="#1D9E75" />}
                </div>
              ))}
            </div>
          )}
          {doctor && doctor.bookable === false && (
            <div className="banner warn" style={{ marginTop: 12 }} data-testid="flow-doctor-not-bookable">
              <Clock size={14} />&nbsp; {doctor.name} is finalising their profile and will be available for booking from <b>{doctor.opens_at_label || "shortly"}</b>. You can view their profile now and book once it opens.
            </div>
          )}
          <button className="btn btn-ink full" style={{ marginTop: 14 }} disabled={!complaint || !specialty || !doctor || doctor.bookable === false} onClick={() => setStep(2)} data-testid="flow-next-1">
            Continue <ArrowRight size={15} />
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="card">
          <Calendar selected={date} onSelect={setDate} />
          {date && (
            <div className="slots" data-testid="flow-slots">
              {slots.map(s => (
                <button key={s.time}
                  className={`slot ${time === s.time ? "sel" : ""} ${!s.available ? "taken" : ""}`}
                  disabled={!s.available}
                  onClick={() => setTime(s.time)}
                  data-testid={`flow-slot-${s.time}`}>
                  {s.time}
                </button>
              ))}
            </div>
          )}
          {date && slots.length > 0 && slots.every(s => !s.available) && (
            <div data-testid="flow-no-slots">
              <p className="slots-hint slots-hint-empty">
                No slots are available for this date. Appointments must be booked at least 4 hours in advance — please pick a later date.
              </p>
              {wlJoined ? (
                <p className="slots-hint" style={{ color: "#15803d", fontWeight: 600 }} data-testid="flow-waitlist-joined">
                  <Check size={14} /> &nbsp;You're on the waitlist — we'll email/SMS you the moment a slot opens.
                </p>
              ) : (
                <button className="btn btn-ghost full" style={{ marginTop: 8 }} onClick={joinWaitlist} disabled={wlBusy} data-testid="flow-waitlist-btn">
                  <BellRing size={14} /> &nbsp;{wlBusy ? "Adding you…" : "Notify me when a slot opens"}
                </button>
              )}
            </div>
          )}
          {date && slots.some(s => s.available) && (
            <p className="slots-hint" data-testid="flow-slots-hint">Appointments can be booked at least 4 hours ahead.</p>
          )}
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button className="btn btn-ghost" onClick={() => setStep(1)}>Back</button>
            <button className="btn btn-ink full" disabled={!date || !time} onClick={() => setStep(3)} data-testid="flow-next-2">Continue <ArrowRight size={15} /></button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="card">
          <h3 className="h" style={{ fontSize: 19, marginTop: 0 }}>Review & pay</h3>
          <div className="rrow"><span>For</span>
            <span data-testid="flow-review-forwho">
              {forWho === "self"
                ? `Myself — ${user.name}`
                : (() => { const m = family.find(x => x.id === forWho); return m ? `${m.relationship} — ${m.name}` : "—"; })()}
            </span>
          </div>
          <div className="rrow"><span>Specialist</span><span>{doctor.name}</span></div>
          <div className="rrow"><span>Specialty</span><span>{specialty}</span></div>
          <div className="rrow"><span>Type</span><span>{need}</span></div>
          <div className="rrow"><span>When</span><span>{date} at {time}</span></div>
          <div className="rrow"><span>Concern</span><span>{complaint}</span></div>
          {discountAmount > 0 ? (
            <>
              <div className="rrow"><span>Standard fee</span><span><s style={{ color: "var(--slate)" }}>₹{BASE_FEE}</s></span></div>
              <div className="rrow" data-testid="flow-review-discount"><span>Premium subscriber discount ({discountPct}%)</span><span style={{ color: "var(--green-deep)" }}>− ₹{discountAmount}</span></div>
              <div className="rrow"><span><b>You pay</b></span><span data-testid="flow-review-fee"><b style={{ color: "var(--green-deep)" }}>₹{finalFee}</b></span></div>
            </>
          ) : (
            <div className="rrow"><span>Fee</span><span data-testid="flow-review-fee">₹{BASE_FEE}</span></div>
          )}
          <p className="prof-hint" style={{ marginTop: 10 }}>After payment, you&apos;ll be able to add notes and upload documents (up to 10) for your consultant.</p>
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button className="btn btn-ghost" onClick={() => setStep(2)}>Back</button>
            <button className="btn btn-green full" onClick={confirm} disabled={busy} data-testid="flow-pay">
              <ShieldCheck size={16} /> {busy ? "Processing…" : `Pay ₹${finalFee} & confirm`}
            </button>
          </div>
        </div>
      )}

      {step === 4 && bookedApptId && (
        <div className="card" data-testid="flow-postbook">
          <div className="banner info" style={{ marginBottom: 14 }}>
            <Check size={14} /> &nbsp;Booking confirmed. You can now share notes and documents with your doctor — or skip and add them later from &ldquo;My appointments&rdquo;.
          </div>

          <h3 className="h" style={{ fontSize: 17, marginTop: 0 }}>Notes for your doctor</h3>
          <p className="lead" style={{ marginTop: 0 }}>Anything you&apos;d like your doctor to know beforehand — symptoms, history, medications, concerns.</p>
          <textarea className="in" rows={4} value={postNotes} onChange={e => setPostNotes(e.target.value)}
                    placeholder="e.g. Chest discomfort started 3 days ago after exertion. Currently taking aspirin 75 mg daily…"
                    data-testid="flow-post-notes" />

          <h3 className="h" style={{ fontSize: 17, marginTop: 18 }}>Documents (up to 10)</h3>
          <p className="lead" style={{ marginTop: 0 }}>Only you and your doctor can see these. The admin cannot view them.</p>
          {bookedFiles.map(f => (
            <div className="filechip" key={f.file_id}>
              <FileText size={15} /> {f.filename}
              <button className="btn btn-ghost btn-sm danger" style={{ marginLeft: "auto" }}
                      onClick={() => removePostBooking(f.file_id, f.filename)}
                      data-testid={`flow-post-del-${f.file_id}`}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          {bookedFiles.length < 10 && (
            <label className="upload" data-testid="flow-post-upload">
              <input type="file" multiple hidden onChange={uploadPostBooking} />
              <Upload size={22} color="#185FA5" />
              <p>Upload reports, scans or prescriptions</p>
              <small>{10 - bookedFiles.length} more allowed · PDF, JPG or PNG</small>
            </label>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button className="btn btn-ghost" onClick={() => onBooked(bookedApptId)} data-testid="flow-skip">
              Skip for now
            </button>
            <button className="btn btn-green full" onClick={saveNotesAndFinish} disabled={busy} data-testid="flow-finish">
              {busy ? "Saving…" : "Save & view appointment"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/* ----------------------------- Appointment detail ------------------------ */
function Detail({ id, user, onBack }) {
  const [appt, setAppt] = useState(null);
  const [tab, setTab] = useState("overview");
  const [resched, setResched] = useState(false);
  const [showWa, setShowWa] = useState(false);
  const [rdate, setRdate] = useState(""); const [rslots, setRslots] = useState([]); const [rtime, setRtime] = useState("");

  const load = () => api.get(`/appointments/${id}`).then(r => setAppt(r.data.appointment));
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (!appt || !rdate) return;
    if (appt.kind === "clinic" && appt.clinic_id) {
      api.get(`/patient/clinic/${appt.doctor_id}/${appt.clinic_id}/slots`, { params: { date: rdate } })
        .then(r => setRslots(r.data.slots || [])).catch(() => setRslots([]));
    } else {
      api.get(`/availability?doctor_id=${appt.doctor_id}&date=${rdate}`).then(r => setRslots(r.data.slots || []));
    }
  }, [rdate, appt]);

  if (!appt) return <p className="lead">Loading…</p>;
  const completed = appt.status === "completed";
  const upcoming = appt.status === "upcoming";
  const isVideo = (appt.consult_type || "").toLowerCase().includes("video");

  const uploadFiles = async (e) => {
    const files = e.target.files; if (!files.length) return;
    const fd = new FormData(); Array.from(files).forEach(f => fd.append("files", f));
    try { await api.post(`/appointments/${id}/files`, fd); toast("Reports uploaded ✓"); load(); }
    catch (err) { toast(formatApiError(err.response?.data?.detail)); }
  };

  const download = async (kind) => {
    try {
      const res = await api.get(`/appointments/${id}/document?kind=${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${kind}-${id}.pdf`; a.click();
    } catch (err) { toast(formatApiError(err.response?.data?.detail) || "Not available yet."); }
  };

  const viewReport = (fid) => window.open(`${API}/files/${fid}`, "_blank");

  const doCancel = async () => {
    if (!window.confirm("Cancel this appointment? This can't be undone.")) return;
    try {
      await api.post(`/appointments/${id}/cancel`);
      toast("Appointment cancelled");
      load();
    } catch (err) { toast(err.response ? formatApiError(err.response.data?.detail) : err.message); }
  };

  const doReschedule = async () => {
    try {
      try {
        await api.post(`/appointments/${id}/reschedule`, { date: rdate, time: rtime });
      } catch (err) {
        if (err.response?.status === 402) {
          const proof = await pay(api, { description: "Reschedule fee", name: user.name, email: user.email });
          await api.post(`/appointments/${id}/reschedule`, { date: rdate, time: rtime, order_id: proof.order_id, payment_id: proof.payment_id, signature: proof.signature });
        } else throw err;
      }
      toast("Appointment rescheduled ✓"); setResched(false); setRdate(""); setRtime(""); load();
    } catch (err) { toast(err.response ? formatApiError(err.response.data?.detail) : err.message); }
  };

  return (
    <>
      <button className="back" onClick={onBack} data-testid="detail-back"><ArrowLeft size={15} /> Back</button>
      <div className="idcard">
        <div>
          <div className="idname">{appt.doctor_name}</div>
          <div className="idspec">{appt.specialty} · {appt.consult_type}</div>
          <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
            <span className={`badge ${appt.status}`}>{appt.status}</span>
            <span style={{ color: "#AFC6DE", fontSize: 13 }}><Clock size={13} /> {appt.date} · {appt.time}</span>
          </div>
        </div>
        <div className="idreg" data-testid="appt-detail-fee">
          Fee
          {appt.discount_amount > 0 ? (
            <>
              <b>₹{appt.fee}</b>
              <small style={{ display: "block", color: "var(--green-deep)", fontSize: 11.5, fontWeight: 500, marginTop: 3 }}>
                <s style={{ opacity: .6 }}>₹{appt.fee_original}</s> · {appt.discount_percent}% subscriber off
              </small>
            </>
          ) : (
            <b>₹{appt.fee}</b>
          )}
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 18 }}>
        <button className={`tab ${tab === "overview" ? "on" : ""}`} onClick={() => setTab("overview")} data-testid="tab-overview">Overview</button>
        {isVideo && upcoming && <button className={`tab ${tab === "consult" ? "on" : ""}`} onClick={() => setTab("consult")} data-testid="tab-consult">Video & chat</button>}
        {completed && <button className={`tab ${tab === "opinion" ? "on" : ""}`} onClick={() => setTab("opinion")} data-testid="tab-opinion">Opinion</button>}
      </div>

      {tab === "overview" && (
        <>
          {upcoming && (
            <div className="instr" style={{ marginBottom: 16 }}>
              <h4>Before your consultation</h4>
              <ul>{INSTRUCTIONS.map((t, i) => <li key={i}>{t}</li>)}</ul>
            </div>
          )}

          {(upcoming || completed) && <div style={{ marginBottom: 16 }}><AddToCalendar appt={appt} /></div>}

          <div className="card" style={{ marginBottom: 16 }}>
            <h4 className="h" style={{ fontSize: 16, marginTop: 0 }}>Your concern</h4>
            <p className="lead">{appt.symptom || "—"}</p>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h4 className="h" style={{ fontSize: 16, marginTop: 0 }}>Your reports</h4>
            {(appt.files || []).map((f, i) => (
              <div className="filechip" key={f.file_id || f.filename || i}><FileText size={15} /> {f.filename}
                <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }} onClick={() => viewReport(f.file_id)}><Eye size={13} /> View</button>
              </div>
            ))}
            {upcoming && (
              <label className="upload" data-testid="upload-report">
                <input type="file" multiple hidden onChange={uploadFiles} />
                <Upload size={22} color="#185FA5" />
                <p>Upload reports, scans or prescriptions</p>
                <small>PDF, JPG or PNG</small>
              </label>
            )}
          </div>

          {(upcoming || appt.status === "missed") && (
            <div className="card">
              {appt.kind === "clinic" && appt.clinic_name && (
                <div className="banner info" style={{ marginBottom: 12, fontSize: 13 }} data-testid="appt-clinic-banner">
                  <MapPin size={13} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                  In-person at <b>{appt.clinic_name}</b>{appt.clinic_address ? ` · ${appt.clinic_address}` : ""}
                  {appt.clinic_phone ? ` · ☎ ${appt.clinic_phone}` : ""}
                </div>
              )}
              {!resched ? (
                <button className="btn btn-ghost full" onClick={() => setResched(true)} data-testid="reschedule-open"><CalIcon size={15} /> Reschedule appointment</button>
              ) : (
                <>
                  <h4 className="h" style={{ fontSize: 16, marginTop: 0 }}>Pick a new date & time</h4>
                  <Calendar selected={rdate} onSelect={setRdate} />
                  {rdate && (
                    <div className="slots">
                      {rslots.map(s => <button key={s.time} className={`slot ${rtime === s.time ? "sel" : ""} ${!s.available ? "taken" : ""}`} disabled={!s.available} onClick={() => setRtime(s.time)}>{s.time}</button>)}
                    </div>
                  )}
                  {rdate && rslots.length === 0 && (
                    <p className="slots-hint slots-hint-empty">{appt.kind === "clinic" ? "This establishment has no slots on this day." : "No slots available — please pick a date at least 4 hours ahead."}</p>
                  )}
                  {rdate && rslots.length > 0 && rslots.every(s => !s.available) && (
                    <p className="slots-hint slots-hint-empty">No slots available — please pick a date at least 4 hours ahead.</p>
                  )}
                  <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                    <button className="btn btn-ghost" onClick={() => setResched(false)}>Cancel</button>
                    <button className="btn btn-ink full" disabled={!rdate || !rtime} onClick={doReschedule} data-testid="reschedule-confirm">Confirm new time</button>
                  </div>
                </>
              )}
              <button className="btn btn-ghost full cancel-appt-btn" style={{ marginTop: 10 }} onClick={doCancel} data-testid="cancel-appt-btn">
                <X size={15} /> Cancel appointment
              </button>
            </div>
          )}
        </>
      )}

      {tab === "consult" && (
        <div style={{ display: "grid", gap: 16 }}>
          <Video room={appt.jitsi_room} />
          <Chat appointmentId={id} user={user} />
        </div>
      )}

      {tab === "opinion" && completed && (
        <div className="card">
          {appt.diagnosis && (
            <>
              <h4 className="h" style={{ fontSize: 16, marginTop: 0 }}>Diagnosis</h4>
              <p className="lead" style={{ whiteSpace: "pre-wrap" }} data-testid="detail-diagnosis">{appt.diagnosis}</p>
            </>
          )}
          <h4 className="h" style={{ fontSize: 16, marginTop: appt.diagnosis ? 16 : 0 }}>Specialist&apos;s opinion</h4>
          <p className="lead" style={{ whiteSpace: "pre-wrap" }}>{appt.doctor_notes || appt.notes || "—"}</p>
          {(appt.medicines && appt.medicines.length > 0) && (
            <>
              <h4 className="h" style={{ fontSize: 16 }}>Prescription</h4>
              <table className="appts-table" style={{ marginTop: 6 }} data-testid="detail-rx-table">
                <thead>
                  <tr><th>#</th><th>Form</th><th>Drug</th><th>Dose</th><th>Amount</th><th>Frequency</th><th>Duration</th></tr>
                </thead>
                <tbody>
                  {appt.medicines.map((m, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td><td>{m.form}</td><td>{m.drug}</td><td>{m.dose}</td>
                      <td>{m.amount}</td><td>{m.frequency}</td><td>{m.duration}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {!appt.medicines?.length && appt.prescription && (
            <><h4 className="h" style={{ fontSize: 16 }}>Prescription</h4>
              <p className="lead" style={{ whiteSpace: "pre-wrap" }}>{appt.prescription}</p></>
          )}
          <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
            {(appt.medicines?.length || appt.prescription) && (
              <>
                <button className="btn btn-ink" onClick={() => download("prescription")} data-testid="download-rx"><Download size={15} /> Prescription PDF</button>
                <button className="btn btn-wa" onClick={() => setShowWa(true)} data-testid="detail-wa-rx">
                  <MessageSquare size={15} /> WhatsApp my Rx
                </button>
              </>
            )}
            {(appt.notes || appt.doctor_notes) && (
              <button className="btn btn-ghost" onClick={() => download("notes")} data-testid="download-notes"><Download size={15} /> Notes PDF</button>
            )}
          </div>
          <ReviewWidget appt={appt} />
        </div>
      )}

      {showWa && (
        <DetailWhatsAppRx appt={appt} onClose={() => setShowWa(false)} />
      )}
    </>
  );
}

/* Detail-page WhatsApp launcher (same backend as the list-page modal). */
function ReviewWidget({ appt }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/appointments/${appt.id}/review`).then(r => {
      if (r.data.review) { setRating(r.data.review.rating); setComment(r.data.review.comment || ""); setSaved(true); }
    }).catch(() => {});
  }, [appt.id]);
  const submit = async () => {
    if (!rating) { toast("Please pick a star rating"); return; }
    setBusy(true);
    try {
      await api.post(`/appointments/${appt.id}/review`, { rating, comment });
      setSaved(true); toast("Thank you for your feedback ✓");
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="review-widget" data-testid="review-widget">
      <h4 className="h" style={{ fontSize: 15, margin: "18px 0 4px" }}>{saved ? "Your rating" : "Rate your consultation"}</h4>
      <p className="lead" style={{ fontSize: 12.5, margin: "0 0 8px" }}>Your feedback is private and helps us maintain quality.</p>
      <div className="review-stars" data-testid="review-stars">
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n} type="button" className={`review-star ${(hover || rating) >= n ? "on" : ""}`}
            onMouseEnter={() => !saved && setHover(n)} onMouseLeave={() => setHover(0)}
            onClick={() => !saved && setRating(n)} disabled={saved} data-testid={`review-star-${n}`}>
            <Star size={24} fill={(hover || rating) >= n ? "currentColor" : "none"} />
          </button>
        ))}
      </div>
      {!saved && (
        <>
          <textarea className="in" rows={2} value={comment} onChange={e => setComment(e.target.value)}
            placeholder="Share more about your experience (optional)" data-testid="review-comment" style={{ marginTop: 10 }} />
          <button className="btn btn-green" style={{ marginTop: 10 }} onClick={submit} disabled={busy} data-testid="review-submit">
            {busy ? "Submitting…" : "Submit rating"}
          </button>
        </>
      )}
      {saved && comment && <p className="lead" style={{ fontSize: 13, marginTop: 8, fontStyle: "italic" }}>"{comment}"</p>}
    </div>
  );
}

function DetailWhatsAppRx({ appt, onClose }) {
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then(r => setMobile(r.data.user?.mobile || ""))
      .catch(() => {});
  }, []);
  const submit = async () => {
    if (!mobile.trim()) { toast("Please enter the WhatsApp number."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/appointments/${appt.id}/whatsapp-rx`, { mobile });
      if (data.channel === "twilio") { toast("Sent to WhatsApp ✓"); setDone({ channel: "twilio" }); }
      else { window.open(data.url, "_blank", "noopener,noreferrer"); setDone({ channel: "walink", url: data.url }); }
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal wa-modal" onClick={e => e.stopPropagation()} data-testid="detail-wa-modal">
        <button className="m-close" onClick={onClose}>✕</button>
        <h3 className="h" style={{ fontSize: 20, marginTop: 0, color: "#1D9E75" }}>
          <MessageSquare size={18} style={{ verticalAlign: "-3px" }} /> Send my prescription on WhatsApp
        </h3>
        <p className="lead" style={{ marginTop: 0 }}>{appt.doctor_name} · {appt.date} · {appt.time}</p>
        {!done && (
          <>
            <div className="field">
              <label>WhatsApp number</label>
              <input className="in" value={mobile} onChange={e => setMobile(e.target.value)}
                     placeholder="+91 98765 43210" data-testid="detail-wa-mobile" />
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn btn-wa" onClick={submit} disabled={busy} data-testid="detail-wa-send">
                <MessageSquare size={14} /> {busy ? "Sending…" : "Send on WhatsApp"}
              </button>
            </div>
          </>
        )}
        {done && done.channel === "twilio" && (
          <div className="banner info">✓ &nbsp;Prescription sent to WhatsApp.</div>
        )}
        {done && done.channel === "walink" && (
          <>
            <div className="banner info" style={{ marginBottom: 10 }}>
              We&apos;ve opened WhatsApp with your prescription ready to send.
            </div>
            <a className="btn btn-wa" href={done.url} target="_blank" rel="noreferrer">
              <MessageSquare size={14} /> Open WhatsApp
            </a>
          </>
        )}
      </div>
    </div>
  );
}

/* -------------------------------- Dashboard ------------------------------ */
function copyText(t) {
  try { navigator.clipboard.writeText(t); toast("Copied ✓"); } catch { /* ignore */ }
}

const REL_OPTIONS = ["Mother", "Father", "Son", "Daughter", "Wife"];

function MyFamily({ onBookFor }) {
  const [members, setMembers] = useState([]);
  const [remaining, setRemaining] = useState(5);
  const [show, setShow] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editMember, setEditMember] = useState(null);
  const [form, setForm] = useState({ name: "", relationship: "", age: "", sex: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/patient/family").then(r => {
    setMembers(r.data.members || []); setRemaining(r.data.remaining ?? 0);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditId(null); setEditMember(null); setForm({ name: "", relationship: "", age: "", sex: "" }); setShow(true); };
  const openEdit = (m) => { setEditId(m.id); setEditMember(m); setForm({ name: m.name || "", relationship: m.relationship || "", age: m.age || "", sex: m.sex || "" }); setShow(true); };

  const submit = async () => {
    if (!form.name.trim()) { toast("Please enter a name."); return; }
    if (!form.relationship) { toast("Please choose the relationship."); return; }
    setBusy(true);
    try {
      if (editId) {
        // age/sex are set-once: only send them if they were blank before.
        const payload = { name: form.name, relationship: form.relationship };
        if (!editMember?.age) payload.age = form.age;
        if (!editMember?.sex) payload.sex = form.sex;
        await api.put(`/patient/family/${editId}`, payload);
        toast("Family member updated ✓");
      } else { await api.post("/patient/family", form); toast("Family member added ✓"); }
      setShow(false); setEditId(null); setEditMember(null); setForm({ name: "", relationship: "", age: "", sex: "" });
      load();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="card fam-card" data-testid="family-section">
      <div className="fam-head">
        <div>
          <h3 className="h" style={{ fontSize: 18, margin: 0 }}><UsersIcon size={17} style={{ verticalAlign: "-3px" }} /> My family</h3>
          <p className="lead" style={{ margin: "4px 0 0" }}>Register your immediate family so booking for them is quick. Age &amp; sex are optional and can be added anytime. {remaining} of 5 slots left.</p>
        </div>
        <button className="btn btn-green" disabled={remaining <= 0} onClick={openAdd} data-testid="family-add-open">
          <Plus size={15} /> Add member
        </button>
      </div>

      {members.length === 0 && <p className="lead" style={{ marginTop: 12 }}>No family members added yet.</p>}
      <div className="fam-list">
        {members.map(m => (
          <div className="fam-row" key={m.id} data-testid={`family-${m.id}`}>
            <span className="ib-avatar patient"><UserRound size={16} /></span>
            <div className="fam-main">
              <b>{m.name} <span className="rel-chip">{m.relationship}</span></b>
              <span className="fam-meta">
                <span className="pid-chip">{m.patient_code}</span>
                <button className="pid-copy" title="Copy ID" onClick={() => copyText(m.patient_code)}><Copy size={12} /></button>
                <small>{[m.age, m.sex].filter(Boolean).join(" · ") || "Age/Sex not set"}</small>
              </span>
            </div>
            <button className="fam-edit" title="Edit" onClick={() => openEdit(m)} data-testid={`family-edit-${m.id}`}><Pencil size={14} /></button>
            <button className="btn btn-green btn-sm fam-book"
                    onClick={() => onBookFor?.(m)} data-testid={`family-book-${m.id}`}>
              <CalIcon size={13} /> Book consultation
            </button>
          </div>
        ))}
      </div>
      {members.length > 0 && <p className="prof-hint" style={{ marginTop: 10 }}>To remove a family member, please contact the admin — only an admin can delete a patient record (DPDP Act, 2023).</p>}

      {show && (
        <div className="modal-bg" onClick={() => setShow(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} data-testid="family-modal">
            <button className="m-close" onClick={() => setShow(false)}>✕</button>
            <h3 className="h" style={{ fontSize: 20, marginTop: 0 }}>{editId ? "Edit family member" : "Add a family member"}</h3>
            <p className="lead" style={{ marginBottom: 14 }}>{editId ? "The Patient ID stays the same." : "A unique Patient ID is assigned automatically."} Only immediate family can be added.</p>
            <div className="field"><label>Full name</label><input className="in" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="family-name" /></div>
            <div className="field"><label>Relationship</label>
              <select className="in" value={form.relationship} onChange={e => setForm({ ...form, relationship: e.target.value })} data-testid="family-relationship">
                <option value="">Select relationship</option>
                {REL_OPTIONS.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div className="field two">
              <div><label>Age {!(editId && editMember?.age) && <span style={{ color: "var(--slate)", fontWeight: 400 }}>(optional)</span>}</label>
                {editId && editMember?.age
                  ? <div className="locked-val" data-testid="family-age-locked">{editMember.age} <Lock size={12} style={{ color: "var(--slate)" }} /></div>
                  : <input className="in" value={form.age} onChange={e => setForm({ ...form, age: e.target.value })} data-testid="family-age" placeholder="e.g. 60" />}
              </div>
              <div><label>Sex {!(editId && editMember?.sex) && <span style={{ color: "var(--slate)", fontWeight: 400 }}>(optional)</span>}</label>
                {editId && editMember?.sex
                  ? <div className="locked-val" data-testid="family-sex-locked">{editMember.sex} <Lock size={12} style={{ color: "var(--slate)" }} /></div>
                  : <select className="in" value={form.sex} onChange={e => setForm({ ...form, sex: e.target.value })} data-testid="family-sex">
                      <option value="">Select</option><option>Male</option><option>Female</option><option>Other</option>
                    </select>}
              </div>
            </div>
            {editId && (editMember?.age || editMember?.sex) && <p className="prof-hint" style={{ marginTop: 0 }}>Age/sex can’t be edited once set — contact admin to change.</p>}
            <button className="btn btn-green full" onClick={submit} disabled={busy} data-testid="family-submit">{busy ? "Saving…" : (editId ? "Save changes" : "Add family member")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------- Health timeline ----------------------------- */
const KIND_COLOR = { clinic: "#1D9E75", peer: "#7c5cff", online: "#185FA5", "": "#185FA5" };
function HealthTimeline() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/patient/me/timeline").then(r => setItems(r.data.timeline || [])).catch(() => setItems([])); }, []);
  const downloadPdf = async () => {
    try {
      const res = await api.get("/patient/me/timeline.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "health-timeline.pdf"; a.click();
    } catch { toast("Could not download timeline"); }
  };
  if (!items) return <p className="lead">Loading…</p>;
  return (
    <div data-testid="health-timeline">
      <div className="estab-header">
        <div>
          <h3 className="h" style={{ fontSize: 20, margin: "0 0 4px" }}>Health timeline</h3>
          <p className="lead" style={{ margin: 0 }}>All your consultations, reports, diagnoses and prescriptions in one place.</p>
        </div>
        <button className="btn btn-ink" onClick={downloadPdf} disabled={!items.length} data-testid="timeline-download"><Download size={15} /> Export PDF</button>
      </div>
      <div className="tl-legend">
        <span><i style={{ background: KIND_COLOR.online }} /> Online</span>
        <span><i style={{ background: KIND_COLOR.clinic }} /> Clinic visit</span>
        <span><i style={{ background: KIND_COLOR.peer }} /> Peer</span>
      </div>
      {items.length === 0 && <div className="card"><p className="lead">No consultations on record yet.</p></div>}
      <div className="tl-list">
        {items.map(it => (
          <div className="tl-item" key={it.id} data-testid={`timeline-item-${it.id}`} style={{ "--tl": KIND_COLOR[it.kind] || KIND_COLOR.online }}>
            <div className="tl-dot" />
            <div className="tl-body card">
              <div className="tl-head">
                <b>{it.doctor_name}</b>
                <span className="tl-type" style={{ background: KIND_COLOR[it.kind] || KIND_COLOR.online }}>{it.kind === "clinic" ? "Clinic" : it.kind === "peer" ? "Peer" : "Online"}</span>
                <span className="tl-date">{it.date} · {it.time}</span>
                <span className={`badge ${it.status}`} style={{ marginLeft: "auto" }}>{it.status}</span>
              </div>
              <div className="tl-spec">{it.specialty} · {it.consult_type}{it.clinic_name ? ` · ${it.clinic_name}` : ""}</div>
              {it.symptom && <p className="tl-line"><b>Concern:</b> {it.symptom}</p>}
              {it.diagnosis && <p className="tl-line"><b>Diagnosis:</b> {it.diagnosis}</p>}
              {it.opinion && <p className="tl-line"><b>Opinion:</b> {it.opinion}</p>}
              {it.rx_summary && <p className="tl-line"><b>Prescription:</b> {it.rx_summary}</p>}
              {it.reports?.length > 0 && <p className="tl-line"><b>Reports:</b> {it.reports.map(r => r.filename).join(", ")}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------- My Profile ----------------------------- */
function Profile({ user }) {
  const { refresh, logout } = useAuth();
  const [age, setAge] = useState(user.age || "");
  const [sex, setSex] = useState(user.sex || "");
  const [savingAS, setSavingAS] = useState(false);
  const [modal, setModal] = useState(null);     // 'email' | 'mobile'
  const [value, setValue] = useState("");
  const [stage, setStage] = useState("form");   // form | otp
  const [code, setCode] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [busy, setBusy] = useState(false);

  const deleteMyData = async () => {
    if (!window.confirm("Delete your personal data? Your account will be anonymised and you'll be signed out. This cannot be undone.")) return;
    try {
      await api.post("/patient/me/delete-data");
      toast("Your data has been deleted.");
      setTimeout(() => logout?.(), 1200);
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };

  const saveAgeSex = async () => {
    const payload = {};
    if (!user.age) payload.age = age;
    if (!user.sex) payload.sex = sex;
    if (!payload.age && !payload.sex) { toast("Nothing to save."); return; }
    setSavingAS(true);
    try { await api.put("/patient/profile", payload); await refresh(); toast("Saved ✓ — this can now only be changed by admin."); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setSavingAS(false); }
  };

  const openChange = (field) => { setModal(field); setValue(""); setStage("form"); setCode(""); setDevOtp(""); };

  const sendOtp = async () => {
    if (!value.trim()) { toast(`Please enter the new ${modal === "email" ? "email" : "phone number"}.`); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/patient/contact/send-otp", { field: modal, value: value.trim() });
      if (data.dev_otp) setDevOtp(data.dev_otp);
      setStage("otp");
      toast(modal === "email" ? "Code sent to the new email." : "Code sent to the new number.");
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/patient/contact/verify", { code });
      await refresh();
      toast(modal === "email" ? "Email updated ✓" : "Phone number updated ✓");
      setModal(null);
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="profile-page">
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 className="h" style={{ fontSize: 18, marginTop: 0 }}>My details</h3>
        <div className="prof-row">
          <span className="prof-k">Full name</span>
          <span className="prof-v">{user.name} <Lock size={12} style={{ color: "var(--slate)" }} /></span>
        </div>
        <p className="prof-hint">To change your name, please contact the admin — only the admin team can update it.</p>
        <div className="prof-row">
          <span className="prof-k">Patient ID</span>
          <span className="prof-v"><span className="pid-chip">{user.patient_code}</span>
            <button className="pid-copy" title="Copy" onClick={() => copyText(user.patient_code)}><Copy size={12} /></button>
            <Lock size={12} style={{ color: "var(--slate)", marginLeft: 6 }} />
          </span>
        </div>
        <p className="prof-hint">Your Patient ID is permanent and cannot be changed.</p>
      </div>

      <div className="card" style={{ marginBottom: 16, borderColor: "#cfe0f0", background: "#f6fafd" }} data-testid="dpdp-notice">
        <h3 className="h" style={{ fontSize: 15, marginTop: 0 }}><ShieldCheck size={15} style={{ verticalAlign: "-2px", color: "#185FA5" }} /> Your data &amp; privacy (DPDP Act, 2023)</h3>
        <p className="lead" style={{ margin: 0 }}>Your personal and health data is processed only to provide your consultations and is protected under the Digital Personal Data Protection Act, 2023. You can update your contact details below, and request correction or erasure of your record anytime by contacting the admin.</p>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 className="h" style={{ fontSize: 16, marginTop: 0 }}>Age &amp; sex {(!user.age || !user.sex) && <span style={{ color: "var(--slate)", fontWeight: 400, fontSize: 13 }}>(optional)</span>}</h3>
        <div className="field two">
          <div>
            <label>Age</label>
            {user.age
              ? <div className="locked-val" data-testid="profile-age-locked">{user.age} <Lock size={12} style={{ color: "var(--slate)" }} /></div>
              : <input className="in" value={age} onChange={e => setAge(e.target.value)} data-testid="profile-age" placeholder="Add your age" />}
          </div>
          <div>
            <label>Sex</label>
            {user.sex
              ? <div className="locked-val" data-testid="profile-sex-locked">{user.sex} <Lock size={12} style={{ color: "var(--slate)" }} /></div>
              : <select className="in" value={sex} onChange={e => setSex(e.target.value)} data-testid="profile-sex">
                  <option value="">Select</option><option>Male</option><option>Female</option><option>Other</option>
                </select>}
          </div>
        </div>
        {(!user.age || !user.sex)
          ? <>
              <p className="prof-hint">You can add this once. After it’s saved it can’t be edited — contact admin to change it later.</p>
              <button className="btn btn-ink" onClick={saveAgeSex} disabled={savingAS} data-testid="profile-save-agesex">{savingAS ? "Saving…" : "Save"}</button>
            </>
          : <p className="prof-hint">Your age and sex are set. To change them, please contact the admin.</p>}
      </div>

      <div className="card">
        <h3 className="h" style={{ fontSize: 16, marginTop: 0 }}>Contact details</h3>
        <p className="lead" style={{ marginTop: 0 }}>Changing your email or phone requires a one-time code sent to the new contact.</p>
        <div className="prof-row">
          <span className="prof-k"><Mail size={14} style={{ verticalAlign: "-2px" }} /> Email</span>
          <span className="prof-v">{user.email || "—"}
            <button className="btn btn-ghost btn-sm" style={{ marginLeft: 10 }} onClick={() => openChange("email")} data-testid="change-email-btn"><Pencil size={13} /> Change</button>
          </span>
        </div>
        <div className="prof-row">
          <span className="prof-k"><Phone size={14} style={{ verticalAlign: "-2px" }} /> Phone</span>
          <span className="prof-v">{user.mobile || "—"}
            <button className="btn btn-ghost btn-sm" style={{ marginLeft: 10 }} onClick={() => openChange("mobile")} data-testid="change-mobile-btn"><Pencil size={13} /> Change</button>
          </span>
        </div>
      </div>

      <div className="card danger-zone" data-testid="dpdp-card">
        <h3 className="h" style={{ fontSize: 16, marginTop: 0, color: "#b42318" }}>Privacy & your data</h3>
        <p className="lead" style={{ marginTop: 0 }}>Under the DPDP Act you can request deletion of your personal data at any time. This anonymises your account and personal details. Medical records are retained in de-identified form as required by law.</p>
        <button className="btn btn-danger" onClick={deleteMyData} data-testid="dpdp-delete-btn"><Trash2 size={15} /> Delete my data</button>
      </div>

      {modal && (
        <div className="modal-bg" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} data-testid="contact-modal">
            <button className="m-close" onClick={() => setModal(null)}>✕</button>
            <h3 className="h" style={{ fontSize: 20, marginTop: 0 }}>Change {modal === "email" ? "email address" : "phone number"}</h3>
            {devOtp && stage === "otp" && <div className="demohint" data-testid="contact-dev-otp">Demo mode — your code is <b>{devOtp}</b></div>}
            {stage === "form" ? (
              <>
                <p className="lead" style={{ marginBottom: 14 }}>Enter your new {modal === "email" ? "email address" : "phone number"}. We’ll send a verification code to it.</p>
                <div className="field"><label>New {modal === "email" ? "email" : "phone number"}</label>
                  <input className="in" value={value} onChange={e => setValue(e.target.value)} data-testid="contact-new-value" placeholder={modal === "email" ? "name@email.com" : "+91 …"} /></div>
                <button className="btn btn-green full" onClick={sendOtp} disabled={busy} data-testid="contact-send-otp">{busy ? "Sending…" : "Send code"}</button>
              </>
            ) : (
              <>
                <p className="lead" style={{ marginBottom: 14 }}>Enter the 6-digit code sent to <b>{value}</b>.</p>
                <div className="field"><label>Verification code</label><input className="in" value={code} onChange={e => setCode(e.target.value)} maxLength={6} data-testid="contact-otp-input" /></div>
                <button className="btn btn-green full" onClick={verify} disabled={busy} data-testid="contact-verify">{busy ? "Verifying…" : "Verify & save"}</button>
                <p className="note" style={{ cursor: "pointer" }} onClick={() => setStage("form")}>Back</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function Dashboard({ user, onBook, onOpen, onInbox }) {
  const [appts, setAppts] = useState([]);
  const [cb, setCb] = useState({ name: user.name, phone: user.mobile || "", preferred_datetime: "", service: "", note: "" });
  const [showCb, setShowCb] = useState(false);
  const [waitlist, setWaitlist] = useState([]);

  useEffect(() => { api.get("/appointments").then(r => setAppts(r.data.appointments || [])); }, []);
  const loadWaitlist = () => api.get("/waitlist/me").then(r => setWaitlist(r.data.waitlist || [])).catch(() => {});
  useEffect(() => { loadWaitlist(); }, []);

  const leaveWaitlist = async (id) => {
    try { await api.delete(`/waitlist/${id}`); setWaitlist(w => w.filter(x => x.id !== id)); toast("Removed from waitlist"); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };

  const sendCb = async () => {
    try { await api.post("/callbacks", cb); toast("We’ll call you back shortly ✓"); setShowCb(false); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <div className="cta">
        <div>
          <h3>Hello, {user.name.split(" ")[0]} 👋</h3>
          <p>Ready for a second opinion? Book a specialist in a few steps.</p>
        </div>
        <button className="btn btn-green" onClick={onBook} data-testid="dash-book">Book a consultation</button>
      </div>

      {user.patient_code && (
        <div className="pid-card" data-testid="my-patient-id">
          <span className="pid-card-ic"><IdCard size={18} /></span>
          <div className="pid-card-main">
            <small>Your Patient ID</small>
            <b>{user.patient_code}</b>
          </div>
          <button className="pid-card-copy" onClick={() => copyText(user.patient_code)} data-testid="copy-my-id"><Copy size={14} /> Copy</button>
        </div>
      )}

      <button className="btn btn-ghost full" style={{ marginBottom: 20 }} onClick={() => onInbox(null)} data-testid="dash-messages">
        <MessageSquare size={15} /> Messages &amp; notifications
      </button>

      <h3 className="h" style={{ fontSize: 18 }}>Your appointments</h3>
      {appts.length === 0 && <p className="lead">No appointments yet — book your first consultation above.</p>}
      {appts.map(a => (
        <div className="appt" key={a.id} onClick={() => onOpen(a.id)} data-testid={`appt-${a.id}`}>
          <div className="tm">{a.date}<br />{a.time}</div>
          <div className="m"><h4>{a.doctor_name}</h4><p>{a.specialty} · {a.consult_type}</p></div>
          <span className={`badge ${a.status}`}>{a.status}</span>
        </div>
      ))}

      {waitlist.length > 0 && (
        <div data-testid="my-waitlist" style={{ marginTop: 24 }}>
          <h3 className="h" style={{ fontSize: 18 }}><BellRing size={16} style={{ verticalAlign: "-2px" }} /> Waitlist</h3>
          <p className="lead" style={{ marginTop: 2 }}>We'll alert you the moment a slot opens for these.</p>
          {waitlist.map(w => (
            <div className="appt" key={w.id} data-testid={`waitlist-${w.id}`}>
              <div className="tm">{w.date}</div>
              <div className="m">
                <h4>{w.doctor_name}</h4>
                <p>{w.clinic_id ? "Clinic visit" : "Online consultation"} · {w.status === "notified" ? "Slot opened — book now!" : "Waiting"}</p>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => leaveWaitlist(w.id)} data-testid={`waitlist-leave-${w.id}`}>Leave</button>
            </div>
          ))}
        </div>
      )}

      <div className="cta" style={{ marginTop: 24, background: "var(--paper)", border: "1px solid var(--line)", color: "var(--ink)" }}>
        <div>
          <h3 className="h" style={{ fontSize: 18 }}>Prefer we call you?</h3>
          <p className="lead">Request a callback and our care team will reach out.</p>
        </div>
        <button className="btn btn-ghost" onClick={() => setShowCb(true)} data-testid="callback-open"><Phone size={15} /> Request a callback</button>
      </div>

      <CalendarSubscribe />

      {showCb && (
        <div className="modal-bg" onClick={() => setShowCb(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <button className="m-close" onClick={() => setShowCb(false)}>✕</button>
            <h3 className="h" style={{ fontSize: 20, marginTop: 0 }}>Request a callback</h3>
            <p className="lead" style={{ marginBottom: 14 }}>Not sure of your symptoms or specialty? Leave your details and our care team will call you.</p>
            <div className="field"><label>Full name</label><input className="in" value={cb.name} onChange={e => setCb({ ...cb, name: e.target.value })} data-testid="callback-name" /></div>
            <div className="field"><label>Contact number</label><input className="in" value={cb.phone} onChange={e => setCb({ ...cb, phone: e.target.value })} data-testid="callback-phone" /></div>
            <div className="field"><label>Preferred date & time</label><input className="in" type="datetime-local" value={cb.preferred_datetime} onChange={e => setCb({ ...cb, preferred_datetime: e.target.value })} data-testid="callback-datetime" /></div>
            <div className="field"><label>Service</label>
              <select className="in" value={cb.service} onChange={e => setCb({ ...cb, service: e.target.value })} data-testid="callback-service">
                <option value="">Select a service…</option>
                {NEEDS.map(n => <option key={n}>{n}</option>)}
              </select>
            </div>
            <div className="field"><label>Note (optional)</label><textarea className="in" value={cb.note} onChange={e => setCb({ ...cb, note: e.target.value })} data-testid="callback-note" /></div>
            <button className="btn btn-green full" onClick={sendCb} data-testid="callback-submit">Request callback</button>
          </div>
        </div>
      )}
    </>
  );
}

/* --------------------------------- Portal -------------------------------- */
/* --------------------- Forced password setup (CS patients) --------------------- */
function ForcePasswordSetup({ user, onDone, onSignOut }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== confirm) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/set-own-password", { new_password: pw });
      toast("Password set ✓ — welcome to your dashboard");
      await onDone();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="force-pw-wrap" data-testid="force-set-password">
      <div className="force-pw-card">
        <div className="force-pw-ic"><ShieldCheck size={22} /></div>
        <h2>Set your own password</h2>
        <p className="lead">Welcome, {user.name.split(" ")[0]}! Your account was created by our customer support team. For your security, please set your own password before accessing your dashboard.</p>
        <div className="force-pw-pid">
          <span>Your Patient ID</span>
          <b data-testid="force-pw-pid">{user.patient_code || "—"}</b>
        </div>
        {err && <p className="err" data-testid="force-pw-error">{err}</p>}
        <div className="field"><label>New password (min 8 chars)</label>
          <input className="in" type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="force-pw-new" />
        </div>
        <div className="field"><label>Confirm password</label>
          <input className="in" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="force-pw-confirm" />
        </div>
        <button className="btn btn-ink full" onClick={submit} disabled={busy} data-testid="force-pw-submit">
          <Lock size={15} /> {busy ? "Setting…" : "Set password & continue"}
        </button>
        <p className="note" style={{ cursor: "pointer", marginTop: 12 }} onClick={onSignOut} data-testid="force-pw-signout">Sign out</p>
      </div>
    </div>
  );
}

/* ----------------------- Clinic Consultation (Premium) ------------------- */
import ClinicSearch from "@/components/portal/ClinicSearch";

function ClinicConsultation({ user, onSubscribed }) {
  const [busy, setBusy] = useState(false);
  const [discount, setDiscount] = useState(0);
  useEffect(() => {
    if (user?.subscribed) {
      api.get("/patient/me/discount").then(r => setDiscount(r.data.clinic_consult || 0)).catch(() => {});
    }
  }, [user?.subscribed]);

  const subscribe = async () => {
    setBusy(true);
    try {
      await api.post("/patient/subscribe");
      toast("Welcome to Premium ✓");
      await onSubscribed?.();
    } catch (e) {
      toast(formatApiError(e.response?.data?.detail) || "Unable to subscribe");
    } finally { setBusy(false); }
  };

  if (user?.subscribed) {
    return (
      <div data-testid="patient-clinic-page">
        <ClinicSearch discountPercent={discount} />
      </div>
    );
  }

  return (
    <div data-testid="patient-clinic-page">
      <div className="card" style={{ textAlign: "center", padding: "40px 24px" }}>
        <span className="ib-avatar patient" style={{ width: 56, height: 56, margin: "0 auto 16px", display: "inline-flex" }}>
          <Building2 size={26} />
        </span>
        <span className="rel-chip" style={{ background: "#fdf2e3", color: "#9a6a12", marginBottom: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Sparkles size={13} /> Premium subscription
        </span>
        <h3 className="h" style={{ fontSize: 22, margin: "8px 0 6px" }}>Clinic Consultation</h3>
        <p className="lead" style={{ maxWidth: 460, margin: "0 auto" }}>
          Book an in-person consultation at a verified doctor&apos;s clinic near you. This premium feature
          lets you meet specialists face-to-face. Subscribe to unlock clinic search by PIN code and specialty.
        </p>
        <div style={{ marginTop: 20 }}>
          <button
            type="button"
            data-testid="patient-clinic-subscribe-btn"
            onClick={subscribe}
            disabled={busy}
            style={{
              background: "#185FA5",
              color: "#fff",
              border: "none",
              padding: "12px 28px",
              borderRadius: 999,
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600,
              fontSize: 14,
              letterSpacing: ".02em",
              cursor: busy ? "wait" : "pointer",
              opacity: busy ? 0.7 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              boxShadow: "0 6px 16px rgba(24,95,165,.25)",
              transition: "transform .15s ease, box-shadow .15s ease, background .15s ease",
            }}
            onMouseEnter={(e) => { if (!busy) { e.currentTarget.style.background = "#0F4A85"; e.currentTarget.style.transform = "translateY(-1px)"; } }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#185FA5"; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <Sparkles size={15} /> {busy ? "Activating…" : "Subscribe Now"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PatientPortal() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const { summary } = useMessaging();
  const [view, setView] = useState("dashboard");
  const [activeId, setActiveId] = useState(null);
  const [convId, setConvId] = useState(null);
  const [sidebar, setSidebar] = useState(false);
  const [navGroupsOpen, setNavGroupsOpen] = useState({ communications: true });

  if (user === undefined) return <PortalShell ribbon="Patient portal" title="2nd Opinion" sub="Patient portal"><p className="lead">Loading…</p></PortalShell>;

  const ribbon = <>Secure patient portal · <b>DPDP Act 2023 compliant</b></>;

  if (!user || user.role !== "patient") {
    return (
      <PortalShell ribbon={ribbon} title="2nd Opinion" sub="Patient portal">
        {user && user.role !== "patient"
          ? <div className="banner warn">You’re signed in as {user.role}. <span style={{ textDecoration: "underline", cursor: "pointer" }} onClick={logout}>Sign out</span> to use the patient portal.</div>
          : <PatientAuth onDone={() => setView("dashboard")} />}
      </PortalShell>
    );
  }

  if (user.must_set_password) {
    return (
      <PortalShell ribbon={ribbon} title="2nd Opinion" sub="Patient portal">
        <ForcePasswordSetup user={user} onDone={refresh} onSignOut={logout} />
      </PortalShell>
    );
  }

  const openInbox = (cid) => { setConvId(cid); setView("inbox"); };
  const bell = (
    <NotificationsBell
      count={summary.unread}
      kinds={["admin_patient", "patient_doctor"]}
      color="#185FA5"
      onOpen={openInbox}
      testid="patient-bell"
      label="Notifications"
    />
  );

  const NAV = [
    { type: "item", key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { type: "item", key: "book", label: "Book consultation", icon: CalIcon },
    { type: "item", key: "appointments", label: "My appointments", icon: ListChecks },
    { type: "item", key: "timeline", label: "Health timeline", icon: ListChecks },
    { type: "item", key: "clinic", label: "Clinic Consultation", icon: Building2 },
    { type: "item", key: "family", label: "My family", icon: UsersIcon },
    {
      type: "group", key: "communications", label: "Communications", icon: MessageSquare, children: [
        { key: "inbox", label: "Inbox", icon: MessageSquare },
        { key: "mail", label: "Mail", icon: Megaphone },
      ],
    },
    { type: "item", key: "profile", label: "My profile", icon: UserRound },
  ];
  const TITLES = { dashboard: "Dashboard", book: "Book a consultation", appointments: "My appointments", clinic: "Clinic Consultation", family: "My family", inbox: "Inbox", mail: "Mail", profile: "My profile", detail: "Appointment" };
  const goto = (key) => { setView(key); setSidebar(false); };
  const toggleGroup = (key) => setNavGroupsOpen((g) => ({ ...g, [key]: !g[key] }));

  return (
    <div className="adm" data-testid="patient-portal">
      <aside className={`side ${sidebar ? "show" : ""}`}>
        <div className="side-brand">
          <div className="side-logo">2</div>
          <div><h1>2nd Opinion</h1><p>Patient portal</p></div>
        </div>
        <nav className="nav">
          {NAV.map((n) => {
            if (n.type === "item") {
              const Icon = n.icon;
              return (
                <button key={n.key} className={view === n.key ? "on" : ""} onClick={() => goto(n.key)} data-testid={`patient-nav-${n.key}`}>
                  <Icon /> {n.label}
                </button>
              );
            }
            const Icon = n.icon;
            const hasActive = n.children.some((c) => c.key === view);
            const isOpen = !!navGroupsOpen[n.key] || hasActive;
            return (
              <div className={`nav-group-wrap ${isOpen ? "open" : ""}`} key={n.key}>
                <button
                  className={`nav-group-btn ${hasActive ? "has-active" : ""}`}
                  onClick={() => toggleGroup(n.key)}
                  aria-expanded={isOpen}
                  data-testid={`patient-nav-group-${n.key}`}
                >
                  <Icon /> <span>{n.label}</span>
                  <ChevronDown className="nav-chev" size={14} />
                </button>
                {isOpen && (
                  <div className="nav-group-children">
                    {n.children.map((c) => {
                      const CIcon = c.icon;
                      return (
                        <button
                          key={c.key}
                          className={view === c.key ? "on" : ""}
                          onClick={() => goto(c.key)}
                          data-testid={`patient-nav-${c.key}`}
                        >
                          <CIcon /> {c.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="side-foot">
          <a onClick={() => nav("/")} data-testid="patient-view-website"><Home size={15} /> View website</a>
          <a onClick={() => logout()} data-testid="patient-signout"><LogOut size={15} /> Sign out</a>
        </div>
      </aside>

      <div className="main">
        <div className="topbar">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button className="burger" onClick={() => setSidebar(s => !s)} data-testid="patient-burger"><MenuIcon size={18} /></button>
            <h2>{TITLES[view] || "Dashboard"}</h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {bell}
            <span className="pill">{user.name}</span>
          </div>
        </div>
        <div className="content">
          <div className="portal portal-embed">
            {view === "dashboard" && <Dashboard user={user} onBook={() => setView("book")} onOpen={(id) => { setActiveId(id); setView("detail"); }} onInbox={openInbox} />}
            {view === "book" && <BookFlow user={user} onCancel={() => setView("dashboard")} onBooked={(id) => { setActiveId(id); setView("detail"); }} />}
            {view === "appointments" && <PatientMyAppointments onOpen={(id) => { setActiveId(id); setView("detail"); }} />}
            {view === "timeline" && <HealthTimeline />}
            {view === "clinic" && <ClinicConsultation user={user} onSubscribed={refresh} />}
            {view === "detail" && <Detail id={activeId} user={user} onBack={() => setView("appointments")} />}
            {view === "family" && (
              <MyFamily onBookFor={(m) => {
                sessionStorage.setItem("booking", JSON.stringify({ for_member_id: m.id, for_member_name: m.name, for_member_relationship: m.relationship }));
                setView("book");
              }} />
            )}
            {view === "profile" && <Profile user={user} />}
            {view === "inbox" && <Inbox role="patient" initialConversationId={convId} accent="#185FA5" />}
            {view === "mail" && <MailView role="patient" accent="#185FA5" />}
          </div>
        </div>
      </div>
    </div>
  );
}
